import React, { useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  Input,
  Text,
  HStack,
  Switch,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import { FormErrorDisplay, RootErrorDisplay } from '@/components/ui/FormErrorDisplay';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { 
  useCreateSpecialityMutation,
  useUpdateSpecialityMutation,
  useGetSpecialityQuery,
} from '../api';
import { useFormErrorHandler } from '@hooks/useFormErrorHandler';

// Schema definition
const specialitySchema = yup.object({
  title: yup
    .string()
    .required('Speciality title is required')
    .min(2, 'Title must be at least 2 characters long')
    .max(100, 'Title must be less than 100 characters'),
  is_shown: yup
    .boolean()
    .required()
});

type SpecialityFormData = yup.InferType<typeof specialitySchema>;

interface SpecialityCreateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number; // ID of speciality to fetch (for hash modal edit mode)
  onSuccess?: () => void;
}

const SpecialityCreateEditModal: React.FC<SpecialityCreateEditModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  // Fetch individual entity if entityId is provided (hash modal edit mode)
  const { data: editingSpeciality, isLoading: isLoadingSpeciality } = useGetSpecialityQuery(entityId!, {
    skip: !entityId,
  });

  const isEditing = Boolean(entityId && editingSpeciality);
  const {
    handleSubmit,
    reset,
    control,
    setError,
    clearErrors,
    formState: { errors, isSubmitting }
  } = useForm<SpecialityFormData>({
    resolver: yupResolver(specialitySchema),
    defaultValues: {
      title: '',
      is_shown: true
    }
  });
  
  const { withErrorHandling, clearAllErrors } = useFormErrorHandler(setError, clearErrors);
  
  const [createSpeciality, { isLoading: isCreating }] = useCreateSpecialityMutation();
  const [updateSpeciality, { isLoading: isUpdating }] = useUpdateSpecialityMutation();
  
  const isLoading = isCreating || isUpdating || isSubmitting;
  const isFormLoading = Boolean(isLoading || (entityId && isLoadingSpeciality));

  // Reset form when modal opens or editingSpeciality changes
  useEffect(() => {
    if (isOpen) {
      // Clear any previous server errors
      clearAllErrors();
      
      if (entityId && editingSpeciality) {
        // Edit mode - prefill with existing data
        reset({ 
          title: editingSpeciality.title,
          is_shown: editingSpeciality.is_shown
        });
      } else if (!entityId) {
        // New mode - reset to empty values
        reset({ title: '', is_shown: true });
      }
      // If entityId exists but editingSpeciality is not loaded yet, 
      // this useEffect will run again when editingSpeciality becomes available
    }
  }, [isOpen, editingSpeciality, entityId, reset, clearAllErrors, isLoadingSpeciality]);

  const onSubmit = async (data: SpecialityFormData) => {
    await withErrorHandling(async () => {
      if (isEditing && editingSpeciality) {
        await updateSpeciality({
          id: editingSpeciality.id,
          data
        }).unwrap();
      } else {
        await createSpeciality(data).unwrap();
      }
      
      onSuccess?.();
      handleClose();
    });
  };

  const handleClose = () => {
    reset({ title: '', is_shown: true });
    clearAllErrors();
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle
          color="brand.700" 
          _dark={{ color: "brand.300" }}
          fontSize="md"
          fontWeight="semibold"
        >
          {isEditing ? 'Edit Speciality' : 'Create New Speciality'}
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        <form onSubmit={handleSubmit(onSubmit)} id="speciality-form">
          <VStack gap={4} align="stretch">
            {/* Display root/general errors using component */}
            <RootErrorDisplay error={errors.root} />
            
            {/* Show loading state when fetching specialty data for editing */}
            {entityId && isLoadingSpeciality ? (
              <Text color="gray.500" textAlign="center" py={4}>
                Loading specialty details...
              </Text>
            ) : (
              <>
                <Box>
                  <Text mb={2} fontSize="sm" fontWeight="medium">
                    Speciality Title
                  </Text>
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder="Enter speciality title"
                        bg="white"
                        _dark={{ bg: "gray.700" }}
                        borderColor={errors.title ? "red.500" : undefined}
                        disabled={isFormLoading}
                      />
                    )}
                  />
                  <FormErrorDisplay error={errors.title} />
                </Box>
                
                <Box>
                  <HStack justify="space-between" align="center">
                    <VStack align="start" gap={1}>
                      <Text fontSize="sm" fontWeight="medium">
                        Visibility
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Show this speciality to users
                      </Text>
                    </VStack>
                    <Controller
                      name="is_shown"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Switch.Root
                          checked={value}
                          onCheckedChange={(details) => onChange(details.checked)}
                          colorPalette="brand"
                          disabled={isFormLoading}
                        >
                          <Switch.HiddenInput />
                          <Switch.Control />
                        </Switch.Root>
                      )}
                    />
                  </HStack>
                </Box>
              </>
            )}
          </VStack>
        </form>
      </DrawerBody>
      
      <DrawerFooter>
        <HStack justify="end" gap={3}>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="speciality-form"
            colorPalette="brand"
            loading={isFormLoading}
            disabled={isFormLoading}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

// Export wrapped versions for create and edit modes
export const SpecialityCreateModal = withHashDrawer(SpecialityCreateEditModal, { 
  action: 'new'  // Create modal: /#/new
});

export const SpecialityEditModal = withHashDrawer(SpecialityCreateEditModal, { 
  action: 'edit' 
});

// Default export for backward compatibility - we'll use create mode
export default SpecialityCreateModal; 